import { Injectable} from '@angular/core';
import { ContractStepModel} from '../../../models/contract.model';
import { DocumentsApiService } from '../../../services/api/documents-api.service';
import { FinanceService } from '../../../services/api/finance.service';
import { CrmService } from '../../../services/api/crm.service';
import { ContractsService } from '../../../services/api/contracts.service';
import { catchError, EMPTY, forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { IMultiPaymentCS, IMultiPaymentResponse, IMultiPaymentStateCS } from '../../../models/finance.model';
import { ContractStepNameService } from './contract-step-name.service';
import { ContractDataService } from './contract-data.service';
import { TranslateService } from '@ngx-translate/core';
import { ContractAvatarsService } from './contract-avatar.service';
interface targetLoadRes {
  guid:string,
  loaded:boolean,
  error:boolean
}
@Injectable({
    providedIn: 'root'
})
export class ContractGetTargetDataService {
  constructor (
    private documentsApiService:DocumentsApiService,
    private financeService:FinanceService,
    private crmService:CrmService,
    private translateService:TranslateService,
    private contractsService:ContractsService,
    private contractStepNameService:ContractStepNameService,
    private contractDataService:ContractDataService,
    private contractAvatarsService:ContractAvatarsService,
  ) { }
  prepareURL(domain:string):string|undefined {
    return  domain? window.location.href.split('://')[0] + '://' + domain +  '/api/v1/' : undefined;
  }
  getStepTargetData(roadmapSteps: ContractStepModel[],currentMemberGuid:string,contractGuid:string, domain?:string): Observable<targetLoadRes[]> {
    this.contractDataService.resetContractTargetData(contractGuid)
    let targetDataStreams$:Observable<targetLoadRes>[] = []
    roadmapSteps.filter(step=>!step?.target_key)
    .forEach(el=>{
      this.contractDataService.subStepDataChanged.next({
      stepName:this.translateService.instant('CONTRACTS.CARD.STEPS.'+(el?.target_type||''))||'',
      step:el
    })})
    roadmapSteps.filter(step=>step.target_key).forEach(step=>targetDataStreams$.push(
      of(step.target_type).pipe(
        switchMap(() => {
          switch (step.target_type) {
            case 'file':
              return this.getDocumentTargetData(step, domain)
            case 'task':
              return this.getTaskTargetData(step, domain)
            case 'payment':
            case 'direct_payment':
              return this.getPaymentTargetData(step)
            case 'sub_contract':
              return this.getSubContractData(step)
            case 'credit_letter':
            case 'general_credit_letter':
              return this.getCreditLettersTargetData(step);
            case 'multi_payment':
              return this.getMultiPaymentTargetData(step, currentMemberGuid)
            default:
              return of ({  guid:"", loaded:false, error:false})
            }
        }),
        tap(()=>this.contractAvatarsService.setAvatarsToShow(step,contractGuid)),
        tap(()=>this.contractStepNameService.shareStepNameData(step)),
        catchError(()=>{
          this.contractDataService.targetsWithErrorLoading.push(step.target_key!);
          if (step.target_type === 'payment') {
            step.payment_data = {
              payer_guid: null,
              recipient_guid: null,
              total: 0,
            };
          } 
          return EMPTY
        })
      )));
   return forkJoin(targetDataStreams$)  
      
  }
  getDocumentTargetData(step: ContractStepModel,domain?:string):Observable<targetLoadRes> {
    //return this.documentsApiService.getFileData(step.target_key||'', this.prepareURL(domain||'')).pipe(
    return this.documentsApiService.getFileData(step.target_key||'').pipe(
      tap(data=>this.contractDataService.contractTargetData[0].targetData.files.push({
          guid:step.target_key!,
          data:data })),
      switchMap(()=>of({guid:step.target_key||'', loaded:true, error:false}))
    )  
  }
  getTaskTargetData(step: ContractStepModel,domain?:string):Observable<targetLoadRes> {
     // return this.crmService.getTask(step.target_key||'',this.prepareURL(domain||'')).pipe(
      return this.crmService.getTask(step.target_key||'').pipe(
        tap(data=>this.contractDataService.contractTargetData[0].targetData.tasks.push({
            guid:step.target_key!,
            data:data })),
        switchMap(()=>of({guid:step.target_key||'', loaded:true, error:false}))
  )  
  }
  getSubContractData(step:ContractStepModel):Observable<targetLoadRes> {
    return this.contractsService.getByGuid(step.target_key!).pipe(
          tap(data=>this.contractDataService.contractTargetData[0].targetData.sub_contracts.push({
              guid:step.target_key!,
              data:data.contract })),
          switchMap(()=>of({guid:step.target_key||'', loaded:true, error:false}))
    )  
  }
  getCreditLettersTargetData(step:ContractStepModel):Observable<targetLoadRes> {
    return this.financeService.getCreditLetter(step.target_key!).pipe(
      tap(data=>this.contractDataService.contractTargetData[0].targetData.credit_letter.push({
          guid:step.target_key!,
          data:data.credit_letter })),
      switchMap(()=>of({guid:step.target_key||'', loaded:true, error:false})) )  
  }
  getPaymentTargetData(step:ContractStepModel):Observable<targetLoadRes> {
    return this.financeService.getPayment(step.target_key!).pipe(
      tap(data=>this.contractDataService.contractTargetData[0].targetData.payments.push({
          guid:step.target_key!,
          data:data.transaction })),
      tap(data => step.payment_data = {            
        payer_guid: data.transaction.payer_guid,
        recipient_guid: data.transaction.recipient_guid,
        recipient_type: data.transaction.recipient_type,
        payer_type: data.transaction.payer_type,
        total: Number(data.transaction.total)
      }),
      switchMap(()=>of({guid:step.target_key||'', loaded:true, error:false})))  
  }
  getMultiPaymentTargetData(step:ContractStepModel,currentMemberGuid:string):Observable<targetLoadRes> {
    return  this.financeService.getMultiPayment(step.target_key!).pipe(
        tap(data=>this.contractDataService.contractTargetData[0].targetData.multi_payments.push({
            guid:step.target_key!,
            data:this.transformMultiPayment(data, step, currentMemberGuid) })),
        switchMap(()=>of({guid:step.target_key||'', loaded:true, error:false}))
  )  
  }
  transformMultiPayment(res:IMultiPaymentResponse, step:ContractStepModel,currentMemberGuid:string):IMultiPaymentStateCS {
    const state:IMultiPaymentStateCS = {
      paymentsData : [],
      signedPayment: res.multi_payment.signed_payment_guid,
      isMemberSign: false,
      isSignPaymentsALot: false,
    };
    step.multi_payment_data = [];
    res.payments_of_multi_payment.map(payment => {
      const currentMemberInPayment = res.members_of_multi_payment
      .filter (member =>
        member.signed_payment_guid === payment.guid &&
        member.contract_member.member.guid === currentMemberGuid
      ) [0];

      state.isMemberSign = !!currentMemberInPayment;

      const paymentOfState:IMultiPaymentCS = {
        payment,
        members: res.members_of_multi_payment.filter(member => member.signed_payment_guid === payment.guid),
        isCurrentMemberSign: !!currentMemberInPayment,
      };
      state.paymentsData.push(paymentOfState);

      const multiPaymentData = {
        total: Number(payment.total),
        recipient_guid: payment.recipient_guid,
        payer_guid: payment.payer_guid,
        payment_guid: payment.guid,
        recipient_type: payment.recipient_type,
        payer_type: payment.payer_type,
      };
      step.multi_payment_data!.push(multiPaymentData);
    });
    state.isSignPaymentsALot = state.paymentsData.filter(payment => payment.members.length).length > 1;
    state.isMemberSign = state.paymentsData.some(el=>el.isCurrentMemberSign===true) 
    return state
  }

}
